import { useState, useEffect } from "react";
import { Server, Save, Loader2, RefreshCw, Eye, EyeOff, Zap, Plus, Trash2, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const apiCall = async (method, url, body = null) => {
  const opts = { method, headers: { "Content-Type": "application/json" }, credentials: "include" };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  return { ok: res.ok, data: await res.json() };
};

const BADGE_COLORS = {
  B: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  C: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  D: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  E: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  F: "bg-pink-500/15 text-pink-400 border-pink-500/20",
};

const ProviderCard = ({ pid, conf, onSave, onTest, onDelete, onToggle }) => {
  const [edit, setEdit] = useState({
    name: conf.name || "", api_key: "", timeout: conf.timeout || 30,
    batch_size: conf.batch_size || 10, concurrent: conf.concurrent || 1,
    group_delay: conf.group_delay || 1.1,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const enabled = conf.enabled !== false;

  useEffect(() => {
    setEdit({
      name: conf.name || "", api_key: "", timeout: conf.timeout || 30,
      batch_size: conf.batch_size || 10, concurrent: conf.concurrent || 1,
      group_delay: conf.group_delay || 1.1,
    });
  }, [conf]);

  const handleSave = async () => {
    setSaving(true);
    const updates = { ...edit };
    if (!updates.api_key) delete updates.api_key;
    await onSave(pid, updates);
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    const result = await onTest(pid);
    setTestResult(result);
    setTesting(false);
  };

  const handleToggle = async () => {
    setToggling(true);
    await onToggle(pid, !enabled);
    setToggling(false);
  };

  return (
    <div className={`border rounded-lg p-5 transition-all ${enabled ? "bg-zinc-800/50 border-zinc-700" : "bg-zinc-900/80 border-zinc-800 opacity-70"}`} data-testid={`provider-card-${pid}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Badge className={`text-xs font-bold px-3 py-1 ${BADGE_COLORS[pid] || "bg-zinc-500/15 text-zinc-400"}`}>
            API {pid}
          </Badge>
          <span className="text-white font-medium text-sm">{conf.name || `Proveedor ${pid}`}</span>
          <Badge className={`text-[10px] ${enabled ? "bg-green-500/15 text-green-400" : "bg-rose-500/15 text-rose-400"}`}>
            {enabled ? "Activo" : "Pausado"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleToggle} disabled={toggling}
            className={`h-8 text-xs ${enabled ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10" : "border-green-500/30 text-green-400 hover:bg-green-500/10"}`}
            data-testid={`toggle-provider-${pid}`}>
            {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : enabled ? <Pause className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />}
            {enabled ? "Pausar" : "Activar"}
          </Button>
          <Button size="sm" onClick={handleTest} disabled={testing}
            className="bg-violet-600 hover:bg-violet-500 text-white h-8 text-xs" data-testid={`test-provider-${pid}`}>
            {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}Test
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs" data-testid={`save-provider-${pid}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Guardar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDelete(true)}
            className="h-8 text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" data-testid={`delete-provider-${pid}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {testResult && (
        <div className={`mb-4 p-3 rounded-lg text-xs ${testResult.status === "OK" ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-rose-500/10 border border-rose-500/20 text-rose-400"}`} data-testid={`test-result-${pid}`}>
          {testResult.status === "OK"
            ? <span>Conexion OK - {testResult.time_ms}ms (timeout: {testResult.timeout_config}s)</span>
            : <span>Error: {testResult.error} ({testResult.time_ms || 0}ms)</span>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Nombre del proveedor</Label>
          <Input value={edit.name} onChange={e => setEdit(s => ({ ...s, name: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" placeholder="Ej: Proveedor Rapido"
            data-testid={`provider-name-${pid}`} />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">
            API Key <span className="text-zinc-600 ml-1">(actual: {conf.api_key_masked || "no configurada"})</span>
          </Label>
          <div className="flex gap-2">
            <Input type={showKey ? "text" : "password"} value={edit.api_key}
              onChange={e => setEdit(s => ({ ...s, api_key: e.target.value }))}
              className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm flex-1"
              placeholder="Dejar vacio para mantener la actual" data-testid={`provider-key-${pid}`} />
            <Button size="sm" variant="ghost" onClick={() => setShowKey(!showKey)} className="h-9 w-9 p-0 text-zinc-400 hover:text-zinc-300">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Timeout (segundos)</Label>
          <Input type="number" min={5} max={120} value={edit.timeout}
            onChange={e => setEdit(s => ({ ...s, timeout: parseInt(e.target.value) || 30 }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" data-testid={`provider-timeout-${pid}`} />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Batch Size (numeros por peticion)</Label>
          <Input type="number" min={1} max={100} value={edit.batch_size}
            onChange={e => setEdit(s => ({ ...s, batch_size: parseInt(e.target.value) || 10 }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" data-testid={`provider-batch-${pid}`} />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Peticiones concurrentes</Label>
          <Input type="number" min={1} max={20} value={edit.concurrent}
            onChange={e => setEdit(s => ({ ...s, concurrent: parseInt(e.target.value) || 1 }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" data-testid={`provider-concurrent-${pid}`} />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Delay entre grupos (seg)</Label>
          <Input type="number" min={0.1} max={10} step={0.1} value={edit.group_delay}
            onChange={e => setEdit(s => ({ ...s, group_delay: parseFloat(e.target.value) || 1.1 }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" data-testid={`provider-delay-${pid}`} />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500">
        <span>Host: <code className="text-zinc-400">{conf.host || "—"}</code></span>
        <span>Endpoint: <code className="text-zinc-400">{conf.endpoint || "—"}</code></span>
      </div>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar Proveedor {pid}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Vas a eliminar permanentemente "{conf.name || `Proveedor ${pid}`}". Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(pid)} className="bg-rose-600 text-white hover:bg-rose-500">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const AddProviderForm = ({ onAdd, onCancel }) => {
  const [form, setForm] = useState({ name: "", api_key: "", host: "", endpoint: "/", timeout: 30, batch_size: 10, concurrent: 1, group_delay: 1.1 });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!form.api_key.trim()) { toast.error("La API Key es obligatoria"); return; }
    if (!form.host.trim()) { toast.error("El host es obligatorio"); return; }
    setSaving(true);
    await onAdd(form);
    setSaving(false);
  };

  return (
    <div className="bg-zinc-800/50 border border-dashed border-green-500/30 rounded-lg p-5" data-testid="add-provider-form">
      <h3 className="text-white font-medium text-sm mb-4 flex items-center gap-2">
        <Plus className="w-4 h-4 text-green-400" /> Nuevo Proveedor de API
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Nombre *</Label>
          <Input value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" placeholder="Ej: Mi Verificador Custom"
            data-testid="new-provider-name" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">API Key *</Label>
          <Input value={form.api_key} onChange={e => setForm(s => ({ ...s, api_key: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" placeholder="Tu API Key de RapidAPI"
            data-testid="new-provider-key" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Host *</Label>
          <Input value={form.host} onChange={e => setForm(s => ({ ...s, host: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" placeholder="ej: api-whatsapp.p.rapidapi.com"
            data-testid="new-provider-host" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Endpoint</Label>
          <Input value={form.endpoint} onChange={e => setForm(s => ({ ...s, endpoint: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" placeholder="/"
            data-testid="new-provider-endpoint" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Timeout (seg)</Label>
          <Input type="number" min={5} max={120} value={form.timeout}
            onChange={e => setForm(s => ({ ...s, timeout: parseInt(e.target.value) || 30 }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" data-testid="new-provider-timeout" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Batch Size</Label>
          <Input type="number" min={1} max={100} value={form.batch_size}
            onChange={e => setForm(s => ({ ...s, batch_size: parseInt(e.target.value) || 10 }))}
            className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm" data-testid="new-provider-batch" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onCancel} className="bg-zinc-900 border-zinc-700 text-zinc-300 h-9 text-xs">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={saving}
          className="bg-green-600 hover:bg-green-500 text-white h-9 text-xs" data-testid="save-new-provider-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
          Crear Proveedor
        </Button>
      </div>
    </div>
  );
};

export const AdminProvidersTab = () => {
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/admin/api-providers`);
      if (ok && data.providers) setProviders(data.providers);
    } catch { toast.error("Error al cargar proveedores"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProviders(); }, []);

  const handleSave = async (pid, updates) => {
    const { ok, data } = await apiCall("PUT", `${API}/admin/api-providers/${pid}`, updates);
    if (ok) { toast.success(data.message || "Guardado"); fetchProviders(); }
    else toast.error(data?.detail || "Error al guardar");
  };

  const handleTest = async (pid) => {
    const { ok, data } = await apiCall("POST", `${API}/admin/api-providers/test/${pid}`);
    return ok ? data : { status: "ERROR", error: data?.detail || "Error" };
  };

  const handleToggle = async (pid, enabled) => {
    const { ok, data } = await apiCall("PUT", `${API}/admin/api-providers/${pid}`, { enabled });
    if (ok) { toast.success(enabled ? `Proveedor ${pid} activado` : `Proveedor ${pid} pausado`); fetchProviders(); }
    else toast.error(data?.detail || "Error");
  };

  const handleDelete = async (pid) => {
    const { ok, data } = await apiCall("DELETE", `${API}/admin/api-providers/${pid}`);
    if (ok) { toast.success(data.message || "Eliminado"); fetchProviders(); }
    else toast.error(data?.detail || "Error al eliminar");
  };

  const handleAdd = async (form) => {
    const { ok, data } = await apiCall("POST", `${API}/admin/api-providers`, form);
    if (ok) { toast.success(data.message || "Proveedor creado"); setShowAdd(false); fetchProviders(); }
    else toast.error(data?.detail || "Error al crear");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const sortedProviders = Object.entries(providers).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="space-y-4" data-testid="admin-providers-section">
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Server className="w-5 h-5 text-violet-400" />Proveedores de API
            </h2>
            <p className="text-sm text-zinc-400">Configura nombre, API Key, timeout y parametros de cada proveedor de verificacion</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowAdd(true)} disabled={showAdd}
              className="bg-green-600 hover:bg-green-500 text-white h-9 text-xs" data-testid="add-provider-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
            <Button onClick={fetchProviders} disabled={loading} variant="outline"
              className="bg-zinc-900 border-zinc-700 text-zinc-300" data-testid="refresh-providers-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid gap-6">
          {showAdd && <AddProviderForm onAdd={handleAdd} onCancel={() => setShowAdd(false)} />}

          {sortedProviders.map(([pid, conf]) => (
            <ProviderCard key={pid} pid={pid} conf={conf}
              onSave={handleSave} onTest={handleTest} onDelete={handleDelete} onToggle={handleToggle} />
          ))}

          {sortedProviders.length === 0 && !showAdd && (
            <div className="text-center py-8 text-zinc-500 text-sm">
              No hay proveedores configurados. Haz clic en "Agregar" para crear uno.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
